import { useEffect, useState } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';

import { history } from 'src/utils/history';
import { ROUTES_URL } from 'src/constants/routes';
import globalAgent from 'src/agent';
import {
    captureRegistrationHandoffFromFragment,
    resetRegistrationHandoffForTests,
    setRegistrationHandoffConsentReader,
    synchronizeRegistrationHandoffConsent,
} from 'src/services/RegistrationHandoff';

const COUNTER_ID = 112761520;
const LEGACY_CONSENT_COOKIE = 'lm_analytics_consent';
const SHARED_CONSENT_COOKIE = 'lm_analytics_consent_v2';
const SHARED_CONSENT_PREFIX = 'v2.';
const CONSENT_CHOICE_KEY = 'lm_analytics_consent_choice';
const CONSENT_MIGRATED_KEY = 'lm_analytics_consent_shared_v2_migrated';
export const CONSENT_CHANGE_EVENT = 'lm-analytics-consent-change';
const REGISTRATION_SUCCESS_GOAL = 'registration_success';
const PURCHASE_CONSENT_EVENT_KEY = 'lm_purchase_consent_event_v1';
const PURCHASE_IDENTITY_RETRY_MS = [1000, 5000, 30000];

export type ConsentChoice = 'accepted' | 'rejected' | 'unknown';
type Ym = ((counter: number, method: string, ...args: unknown[]) => void) & {
    __lmStub?: boolean;
    a?: unknown[][];
    l?: number;
};

declare global {
    interface Window {
        ym?: Ym;
        disableYaCounter112761520?: boolean;
    }
}

let started = false;
let vendorLoaded = false;
let vendorInitialized = false;
let generation = 0;
let observedConsent: ConsentChoice = 'unknown';
let lastHitPath = '';
let entryHitPath = '/';
let entryHitSent = false;
let registrationGoalPending = false;
let registrationGoalDelivered = false;
let entryYclid = '';
let lastPurchaseIdentityPayload = '';
let purchaseIdentityAuthenticated = false;
let purchaseIdentityAccountId = '';
let purchaseIdentityGeneration = 0;
let purchaseIdentityRetryAttempt = 0;
let purchaseIdentityRetryTimer: number | null = null;

function cookieValues(name: string) {
    return document.cookie
        .split(';')
        .map((item) => item.trim())
        .filter((item) => item.startsWith(`${name}=`))
        .map((item) => item.slice(name.length + 1));
}

function sharedCookieChoice(): ConsentChoice {
    const values = cookieValues(SHARED_CONSENT_COOKIE);
    if (values.includes(`${SHARED_CONSENT_PREFIX}rejected`)) return 'rejected';
    if (values.includes(`${SHARED_CONSENT_PREFIX}accepted`)) return 'accepted';
    return 'unknown';
}

function legacyCookieChoice(): ConsentChoice {
    const values = cookieValues(LEGACY_CONSENT_COOKIE);
    if (values.includes('rejected')) return 'rejected';
    if (values.includes('accepted')) return 'accepted';
    return 'unknown';
}

export function analyticsConsentChoice() {
    return sharedCookieChoice();
}

function persistSharedChoice(choice: Exclude<ConsentChoice, 'unknown'>) {
    try {
        window.localStorage.setItem(CONSENT_CHOICE_KEY, choice);
        window.localStorage.setItem(CONSENT_MIGRATED_KEY, '1');
    } catch {}
    try {
        const secure = window.location.protocol === 'https:' ? '; Secure' : '';
        const ownedHost =
            window.location.hostname === 'leadsmania.ru' || window.location.hostname.endsWith('.leadsmania.ru');
        document.cookie = `${LEGACY_CONSENT_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
        document.cookie = `${SHARED_CONSENT_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
        if (ownedHost) {
            document.cookie = `${LEGACY_CONSENT_COOKIE}=; Max-Age=0; Path=/; Domain=.leadsmania.ru; SameSite=Lax${secure}`;
            document.cookie = `${SHARED_CONSENT_COOKIE}=; Max-Age=0; Path=/; Domain=.leadsmania.ru; SameSite=Lax${secure}`;
            document.cookie = `${SHARED_CONSENT_COOKIE}=${SHARED_CONSENT_PREFIX}${choice}; Max-Age=31536000; Path=/; Domain=.leadsmania.ru; SameSite=Lax${secure}`;
        } else {
            document.cookie = `${SHARED_CONSENT_COOKIE}=${SHARED_CONSENT_PREFIX}${choice}; Max-Age=31536000; Path=/; SameSite=Lax${secure}`;
        }
    } catch {}
}

function migrateConsentChoice(): ConsentChoice {
    let migrated = false;
    let stored = '';
    try {
        migrated = window.localStorage.getItem(CONSENT_MIGRATED_KEY) === '1';
        stored = window.localStorage.getItem(CONSENT_CHOICE_KEY) || '';
    } catch {}
    const shared = sharedCookieChoice();
    if (shared !== 'unknown') return shared;
    if (migrated) return 'unknown';
    const legacy = legacyCookieChoice();
    const selected =
        legacy !== 'unknown' ? legacy : stored === 'accepted' || stored === 'rejected' ? stored : 'unknown';
    if (selected !== 'unknown') persistSharedChoice(selected);
    try {
        window.localStorage.setItem(CONSENT_MIGRATED_KEY, '1');
    } catch {}
    return selected;
}

function safeUtmValue(name: string, value: string) {
    const text = value.trim().replace(/\s+/g, ' ').slice(0, 120);
    if (!text || /(?:@|https?:\/\/|t\.me\/)/i.test(text)) return '';
    if (name === 'utm_source' || name === 'utm_medium') {
        return text.length <= 80 && /^[A-Za-z0-9._~/-]+$/.test(text) ? text : '';
    }
    if (name === 'utm_campaign' || name === 'utm_content') {
        return /^[A-Za-z0-9._~-]+$/.test(text) ? text : '';
    }
    if (/\+?\d[\d\s()-]{7,}/.test(text)) return '';
    return /^[A-Za-zА-Яа-яЁё0-9 ._~/-]+$/.test(text) ? text : '';
}

function safeEntryHitPath() {
    try {
        const url = new URL(window.location.href);
        const pathname = url.pathname.length <= 240 && /^\/[a-zA-Z0-9._~/%-]*$/.test(url.pathname) ? url.pathname : '/';
        const query = new URLSearchParams();
        const yclid = url.searchParams.get('yclid') || '';
        entryYclid = /^[A-Za-z0-9_-]{8,255}$/.test(yclid) ? yclid : '';
        if (entryYclid) query.set('yclid', entryYclid);
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((name) => {
            const value = safeUtmValue(name, url.searchParams.get(name) || '');
            if (value) query.set(name, value);
        });
        const suffix = query.toString();
        return suffix ? `${pathname}?${suffix}` : pathname;
    } catch {
        return '/';
    }
}

function consentEventId(accountId: string, decision: 'accepted' | 'rejected') {
    try {
        const key = `${PURCHASE_CONSENT_EVENT_KEY}:${accountId}`;
        const existing = JSON.parse(window.localStorage.getItem(key) || '{}');
        if (existing.decision === decision && /^[0-9a-f-]{36}$/i.test(existing.eventId || '')) {
            return {
                eventId: existing.eventId as string,
                previousEventId: /^[0-9a-f-]{36}$/i.test(existing.previousEventId || '')
                    ? (existing.previousEventId as string)
                    : '',
            };
        }
        const previousEventId = /^[0-9a-f-]{36}$/i.test(existing.eventId || '') ? existing.eventId : '';
        const eventId =
            window.crypto?.randomUUID?.() ||
            'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
                const random = Math.floor(Math.random() * 16);
                return (character === 'x' ? random : (random & 0x3) | 0x8).toString(16);
            });
        window.localStorage.setItem(key, JSON.stringify({ decision, eventId, previousEventId }));
        return { eventId, previousEventId };
    } catch {
        return { eventId: '', previousEventId: '' };
    }
}

function clearPurchaseIdentityRetry() {
    if (purchaseIdentityRetryTimer !== null) window.clearTimeout(purchaseIdentityRetryTimer);
    purchaseIdentityRetryTimer = null;
}

function schedulePurchaseIdentityRetry(expectedGeneration: number) {
    if (purchaseIdentityRetryAttempt >= PURCHASE_IDENTITY_RETRY_MS.length) return;
    const delay = PURCHASE_IDENTITY_RETRY_MS[purchaseIdentityRetryAttempt++];
    clearPurchaseIdentityRetry();
    purchaseIdentityRetryTimer = window.setTimeout(() => {
        purchaseIdentityRetryTimer = null;
        if (expectedGeneration === purchaseIdentityGeneration) synchronizePurchaseIdentity();
    }, delay);
}

function postPurchaseIdentity(
    decision: 'accepted' | 'rejected',
    clientId = '',
    expectedGeneration = purchaseIdentityGeneration
) {
    if (
        !purchaseIdentityAuthenticated ||
        !purchaseIdentityAccountId ||
        expectedGeneration !== purchaseIdentityGeneration ||
        analyticsConsentChoice() !== decision
    )
        return;
    const { eventId, previousEventId } = consentEventId(purchaseIdentityAccountId, decision);
    if (!eventId) return;
    const body = {
        event_id: eventId,
        ...(previousEventId ? { previous_event_id: previousEventId } : {}),
        decision,
        account_id: purchaseIdentityAccountId,
        ...(decision === 'accepted' && clientId ? { client_id: clientId } : {}),
        ...(decision === 'accepted' && entryYclid ? { yclid: entryYclid } : {}),
    };
    const fingerprint = `${purchaseIdentityAccountId}:${JSON.stringify(body)}`;
    if (fingerprint === lastPurchaseIdentityPayload) return;
    lastPurchaseIdentityPayload = fingerprint;
    void globalAgent
        .post('/acquisition/purchase-identity/', body)
        .then(() => {
            if (expectedGeneration === purchaseIdentityGeneration) purchaseIdentityRetryAttempt = 0;
        })
        .catch((error: { response?: { status?: number } }) => {
            if (lastPurchaseIdentityPayload === fingerprint) lastPurchaseIdentityPayload = '';
            if (expectedGeneration !== purchaseIdentityGeneration) return;
            const status = Number(error?.response?.status || 0);
            if (status === 0 || status === 429 || status >= 500) schedulePurchaseIdentityRetry(expectedGeneration);
        });
}

export function synchronizePurchaseIdentity() {
    if (!purchaseIdentityAuthenticated || !purchaseIdentityAccountId) return;
    const expectedGeneration = purchaseIdentityGeneration;
    const choice = analyticsConsentChoice();
    if (choice === 'rejected') {
        postPurchaseIdentity('rejected', '', expectedGeneration);
        return;
    }
    if (choice !== 'accepted' || !vendorInitialized) return;
    window.ym?.(COUNTER_ID, 'getClientID', (clientId: unknown) => {
        if (
            expectedGeneration !== purchaseIdentityGeneration ||
            !purchaseIdentityAuthenticated ||
            analyticsConsentChoice() !== 'accepted'
        )
            return;
        const value = String(clientId || '');
        if (/^[0-9]{5,32}$/.test(value)) postPurchaseIdentity('accepted', value, expectedGeneration);
    });
}

export function notifyPurchaseIdentityAuthenticated(accountId?: string | number | null) {
    const normalized = String(accountId ?? '');
    const authenticated = /^[1-9][0-9]*$/.test(normalized);
    if (purchaseIdentityAuthenticated === authenticated && purchaseIdentityAccountId === normalized) {
        if (authenticated) synchronizePurchaseIdentity();
        return;
    }
    purchaseIdentityAuthenticated = authenticated;
    purchaseIdentityAccountId = authenticated ? normalized : '';
    purchaseIdentityGeneration += 1;
    purchaseIdentityRetryAttempt = 0;
    lastPurchaseIdentityPayload = '';
    clearPurchaseIdentityRetry();
    synchronizePurchaseIdentity();
}

function sendHit() {
    const currentPath = window.location.pathname || '/';
    if (!vendorInitialized || analyticsConsentChoice() !== 'accepted' || currentPath === lastHitPath) return;
    try {
        window.ym?.(COUNTER_ID, 'hit', entryHitSent ? currentPath : entryHitPath);
        entryHitSent = true;
        lastHitPath = currentPath;
    } catch {}
}

function flushRegistrationGoal() {
    if (
        !registrationGoalPending ||
        registrationGoalDelivered ||
        !vendorInitialized ||
        analyticsConsentChoice() !== 'accepted'
    )
        return;
    try {
        window.ym?.(COUNTER_ID, 'reachGoal', REGISTRATION_SUCCESS_GOAL);
        registrationGoalPending = false;
        registrationGoalDelivered = true;
    } catch {}
}

function initializeVendor(currentGeneration: number) {
    if (
        currentGeneration !== generation ||
        vendorInitialized ||
        analyticsConsentChoice() !== 'accepted' ||
        typeof window.ym !== 'function'
    )
        return;
    try {
        window.ym(COUNTER_ID, 'init', {
            defer: true,
            clickmap: true,
            trackLinks: true,
            accurateTrackBounce: true,
        });
        vendorInitialized = true;
        sendHit();
        flushRegistrationGoal();
        synchronizePurchaseIdentity();
    } catch {
        vendorInitialized = false;
    }
}

function loadVendor(currentGeneration: number) {
    if (analyticsConsentChoice() !== 'accepted') return;
    window.disableYaCounter112761520 = false;
    document.querySelectorAll('input, textarea').forEach((field) => field.classList.add('ym-disable-keys'));
    if (vendorLoaded) {
        initializeVendor(currentGeneration);
        return;
    }
    if (document.querySelector('script[data-lm-registration-metrika][data-lm-state="loading"]')) return;
    if (typeof window.ym !== 'function') {
        const stub = function ym(...args: unknown[]) {
            (stub.a = stub.a || []).push(args);
        } as Ym;
        stub.a = [];
        stub.l = Date.now();
        stub.__lmStub = true;
        window.ym = stub;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://mc.yandex.ru/metrika/tag.js?id=${COUNTER_ID}`;
    script.dataset.lmRegistrationMetrika = '';
    script.dataset.lmState = 'loading';
    script.addEventListener('load', () => {
        script.dataset.lmState = 'loaded';
        vendorLoaded = true;
        initializeVendor(currentGeneration);
    });
    script.addEventListener('error', () => script.remove());
    document.head.append(script);
}

function stopVendor() {
    document
        .querySelectorAll('script[data-lm-registration-metrika][data-lm-state="loading"]')
        .forEach((script) => script.remove());
    if (vendorInitialized) {
        try {
            window.ym?.(COUNTER_ID, 'destruct');
        } catch {}
    }
    vendorInitialized = false;
    lastHitPath = '';
    window.disableYaCounter112761520 = true;
}

function synchronizeConsent() {
    const next = analyticsConsentChoice();
    if (next === observedConsent) return;
    observedConsent = next;
    synchronizeRegistrationHandoffConsent(next);
    generation += 1;
    if (next === 'accepted') loadVendor(generation);
    else {
        registrationGoalPending = false;
        stopVendor();
    }
    lastPurchaseIdentityPayload = '';
    purchaseIdentityRetryAttempt = 0;
    clearPurchaseIdentityRetry();
}

export function setAnalyticsConsentChoice(choice: Exclude<ConsentChoice, 'unknown'>) {
    persistSharedChoice(choice);
    window.dispatchEvent(new Event(CONSENT_CHANGE_EVENT));
    window.queueMicrotask(synchronizePurchaseIdentity);
}

export function recordRegistrationSuccessGoal() {
    if (registrationGoalDelivered || analyticsConsentChoice() === 'rejected') return false;
    registrationGoalPending = true;
    if (analyticsConsentChoice() === 'accepted') {
        loadVendor(generation);
        flushRegistrationGoal();
    }
    return true;
}

export function startRegistrationAnalytics() {
    if (started) return () => {};
    started = true;
    setRegistrationHandoffConsentReader(() => sharedCookieChoice() === 'accepted');
    captureRegistrationHandoffFromFragment();
    synchronizeRegistrationHandoffConsent(sharedCookieChoice());
    entryHitPath = safeEntryHitPath();
    entryHitSent = false;
    observedConsent = migrateConsentChoice();
    window.disableYaCounter112761520 = observedConsent !== 'accepted';
    if (observedConsent === 'accepted') loadVendor(generation);
    const unlisten = history.listen(sendHit);
    const synchronize = () => {
        synchronizeConsent();
        synchronizePurchaseIdentity();
    };
    window.addEventListener(CONSENT_CHANGE_EVENT, synchronize);
    window.addEventListener('focus', synchronize);
    document.addEventListener('visibilitychange', synchronize);
    return () => {
        unlisten();
        window.removeEventListener(CONSENT_CHANGE_EVENT, synchronize);
        window.removeEventListener('focus', synchronize);
        document.removeEventListener('visibilitychange', synchronize);
        generation += 1;
        stopVendor();
        setRegistrationHandoffConsentReader(() => false);
        started = false;
    };
}

export function RegistrationAnalytics() {
    useEffect(() => startRegistrationAnalytics(), []);
    return <AnalyticsConsentControl />;
}

function AnalyticsConsentControl() {
    const [choice, setChoice] = useState<ConsentChoice>(() => analyticsConsentChoice());
    const [open, setOpen] = useState(() => choice === 'unknown');
    const [pathname, setPathname] = useState(() => window.location.pathname);

    useEffect(() => {
        const synchronize = (closeOnDecision = false) => {
            const next = analyticsConsentChoice();
            setChoice(next);
            if (next === 'unknown') setOpen(true);
            else if (closeOnDecision) setOpen(false);
        };
        const synchronizeConsentChange = () => synchronize(true);
        const synchronizeCurrentChoice = () => synchronize();
        const unlisten = history.listen(({ pathname: nextPathname }) => setPathname(nextPathname));
        window.addEventListener(CONSENT_CHANGE_EVENT, synchronizeConsentChange);
        window.addEventListener('focus', synchronizeCurrentChoice);
        window.queueMicrotask(synchronizeCurrentChoice);
        return () => {
            unlisten();
            window.removeEventListener(CONSENT_CHANGE_EVENT, synchronizeConsentChange);
            window.removeEventListener('focus', synchronizeCurrentChoice);
        };
    }, []);

    const select = (next: Exclude<ConsentChoice, 'unknown'>) => {
        setAnalyticsConsentChoice(next);
        setChoice(next);
        setOpen(false);
    };

    const normalizedPathname = pathname.replace(/\/+$/, '') || '/';
    if (choice === 'unknown' && normalizedPathname === ROUTES_URL.REGISTER) return null;

    if (!open && choice !== 'unknown') {
        return (
            <Button
                size="small"
                variant="text"
                onClick={() => setOpen(true)}
                sx={{ position: 'fixed', left: 12, bottom: 10, zIndex: 1200, bgcolor: 'background.paper' }}
            >
                Настройки аналитики
            </Button>
        );
    }

    return (
        <Paper
            role="dialog"
            aria-label="Настройки аналитики"
            elevation={4}
            sx={{
                position: 'fixed',
                right: { xs: 10, sm: 16 },
                bottom: { xs: 10, sm: 16 },
                zIndex: 1400,
                width: { xs: 'calc(100vw - 20px)', sm: 390 },
                p: 2,
                border: 1,
                borderColor: 'divider',
            }}
        >
            <Stack spacing={1.5}>
                <Box>
                    <Typography fontWeight={600}>Аналитика сайта</Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Собираем аналитику, чтобы улучшать сайт.
                    </Typography>
                </Box>
                <Stack
                    direction={{ xs: 'column-reverse', sm: 'row' }}
                    spacing={1}
                    justifyContent="flex-end"
                >
                    <Button
                        variant={choice === 'rejected' ? 'contained' : 'outlined'}
                        color="inherit"
                        onClick={() => select('rejected')}
                    >
                        Без аналитики
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => select('accepted')}
                    >
                        Разрешить
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    );
}

export function resetRegistrationAnalyticsForTests() {
    document.querySelectorAll('script[data-lm-registration-metrika]').forEach((script) => script.remove());
    started = false;
    vendorLoaded = false;
    vendorInitialized = false;
    generation += 1;
    observedConsent = 'unknown';
    lastHitPath = '';
    entryHitPath = '/';
    entryHitSent = false;
    registrationGoalPending = false;
    registrationGoalDelivered = false;
    entryYclid = '';
    lastPurchaseIdentityPayload = '';
    purchaseIdentityAuthenticated = false;
    purchaseIdentityAccountId = '';
    purchaseIdentityGeneration += 1;
    purchaseIdentityRetryAttempt = 0;
    clearPurchaseIdentityRetry();
    resetRegistrationHandoffForTests();
    delete window.ym;
    delete window.disableYaCounter112761520;
}
